// src/pages/RegistrarCalificaciones.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/formularios.css';
import '../styles/styles.css';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveApiBaseList } from '../utils/apiBase';

const fasePorGrado = (grado) => {
  const g = parseInt(grado, 10);
  if (Number.isNaN(g)) return '';
  if (g === 1 || g === 2) return 'Fase 3';
  if (g === 3 || g === 4) return 'Fase 4';
  if (g === 5 || g === 6) return 'Fase 5';
  return '';
};

const RegistrarCalificaciones = () => {
  const [grupos, setGrupos] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [alumnos, setAlumnos] = useState([]);

  const [fase, setFase] = useState('');
  const [trimestre, setTrimestre] = useState('');
  const [grupo, setGrupo] = useState('');
  const [alumno, setAlumno] = useState('');
  const [materia, setMateria] = useState('');

  const [calificacion, setCalificacion] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [sugerencias, setSugerencias] = useState('');

  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [loadingMaterias, setLoadingMaterias] = useState(false);
  const [loadingAlumnos, setLoadingAlumnos] = useState(false);
  const [cargandoCalificacion, setCargandoCalificacion] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [mensaje, setMensaje] = useState('');
  const [mensajeTipo, setMensajeTipo] = useState('info');

  const navigate = useNavigate();
  const location = useLocation();

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;

    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;

      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch {
            // mantiene mensaje original
          }

          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (!trimmed) {
          return { data: {}, baseUrl: normalizedBase };
        }

        try {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase };
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: no se pudo parsear JSON desde ${url}`, parseErr);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const cargarGrupos = useCallback(async () => {
    try {
      setLoadingGrupos(true);
      const { data } = await fetchJsonWithFallback('/grupos/activos');
      setGrupos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Error al cargar grupos:', error);
      setMensaje(error.message || 'Error al cargar los grupos activos.');
      setMensajeTipo('danger');
    } finally {
      setLoadingGrupos(false);
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    cargarGrupos();
  }, [cargarGrupos]);

  const cargarMaterias = useCallback(async (idGrupo) => {
    if (!idGrupo) {
      setMaterias([]);
      return;
    }

    try {
      setLoadingMaterias(true);
      const { data } = await fetchJsonWithFallback(`/calificacionesmod/grupo/${idGrupo}/materias`);
      setMaterias(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Error al cargar materias:', error);
      setMensaje(error.message || 'Error al cargar las materias del grupo.');
      setMensajeTipo('danger');
      setMaterias([]);
    } finally {
      setLoadingMaterias(false);
    }
  }, [fetchJsonWithFallback]);

  const cargarAlumnos = useCallback(async (idGrupo) => {
    if (!idGrupo) {
      setAlumnos([]);
      return;
    }

    try {
      setLoadingAlumnos(true);
      const { data } = await fetchJsonWithFallback(`/grupos/${idGrupo}/alumnos-detalle`);
      setAlumnos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Error al cargar alumnos:', error);
      setMensaje(error.message || 'Error al cargar los alumnos del grupo.');
      setMensajeTipo('danger');
      setAlumnos([]);
    } finally {
      setLoadingAlumnos(false);
    }
  }, [fetchJsonWithFallback]);

  const limpiarCampos = useCallback(() => {
    setTrimestre('');
    setMateria('');
    setAlumno('');
    setCalificacion('');
    setObservaciones('');
    setSugerencias('');
    setMensaje('');
    setMensajeTipo('info');
  }, []);

  const manejarCambioGrupo = async (event) => {
    const value = event.target.value;
    setGrupo(value);

    const infoGrupo = grupos.find((g) => String(g.IdGrupo) === value);
    setFase(infoGrupo ? fasePorGrado(infoGrupo.Grado) : '');

    setMateria('');
    setAlumno('');
  setCalificacion('');
  setObservaciones('');
  setSugerencias('');

    await Promise.all([cargarMaterias(value), cargarAlumnos(value)]);
  };

  const manejarCambioMateria = (event) => {
    setMateria(event.target.value);
  };

  const manejarCambioTrimestre = (event) => {
    setTrimestre(event.target.value);
  };

  const manejarCambioAlumno = (event) => {
    setAlumno(event.target.value);
  };

  useEffect(() => {
    const consultarCalificacion = async () => {
      if (!grupo || !materia || !trimestre || !alumno) {
        return;
      }

      try {
        setCargandoCalificacion(true);
        setMensaje('');

        const params = new URLSearchParams({
          grupoId: grupo,
          materiaId: materia,
          trimestre,
          idAlumno: alumno
        });

        const { data } = await fetchJsonWithFallback(`/calificacionesmod/calificacion?${params.toString()}`);

        if (data?.found) {
          const numeroCalificacion = Number(data.calificacion);
          const esValida = Number.isFinite(numeroCalificacion) && numeroCalificacion >= 6 && numeroCalificacion <= 10;
          const calificacionEntera = esValida ? String(Math.round(numeroCalificacion)) : '';
          setCalificacion(calificacionEntera);
          setObservaciones(data.observaciones ?? '');
          setSugerencias(data.sugerencias ?? '');
          setMensaje('Calificación cargada correctamente.');
          setMensajeTipo('success');
          if (data.fase) {
            setFase(`Fase ${data.fase}`);
          }
        } else {
          setCalificacion('');
          setObservaciones('');
          setSugerencias('');
          setMensaje('No hay calificación registrada para esta combinación. Se creará una nueva al guardar.');
          setMensajeTipo('info');
          if (data?.fase) {
            setFase(`Fase ${data.fase}`);
          }
        }
      } catch (error) {
        console.error('❌ Error al consultar la calificación:', error);
        setMensaje(error.message || 'Error al consultar la calificación.');
        setMensajeTipo('danger');
        setCalificacion('');
        setObservaciones('');
        setSugerencias('');
      } finally {
        setCargandoCalificacion(false);
      }
    };

    consultarCalificacion();
  }, [grupo, materia, trimestre, alumno, fetchJsonWithFallback]);

  const manejarGuardar = async (event) => {
    event.preventDefault();

    if (!grupo || !materia || !trimestre || !alumno) {
      setMensaje('Selecciona grupo, materia, trimestre y alumno antes de guardar.');
      setMensajeTipo('warning');
      return;
    }

    if (!calificacion) {
      setMensaje('Ingresa una calificación entre 6 y 10.');
      setMensajeTipo('warning');
      return;
    }

    const calificacionNumero = parseInt(calificacion, 10);
    if (Number.isNaN(calificacionNumero) || calificacionNumero < 6 || calificacionNumero > 10) {
      setMensaje('La calificación debe ser un número entre 6 y 10.');
      setMensajeTipo('warning');
      return;
    }

    try {
      setGuardando(true);
      setMensaje('');

      const { data: saveResult } = await fetchJsonWithFallback('/calificacionesmod/calificacion', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grupoId: grupo,
          materiaId: materia,
          idAlumno: alumno,
          trimestre,
          calificacion: calificacionNumero,
          observaciones,
          sugerencias
        })
      });

      if (saveResult?.success === false) {
        throw new Error(saveResult?.error || 'No fue posible guardar la calificación.');
      }

      setMensaje('Calificación modificada correctamente.');
      setMensajeTipo('success');
      await Swal.fire({
        icon: 'success',
        title: 'Modificación exitosa',
        text: 'La calificación se actualizó correctamente.',
        confirmButtonColor: '#198754',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('❌ Error al guardar calificación:', error);
      setMensaje(error.message || 'Error al guardar la calificación.');
      setMensajeTipo('danger');
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Error al guardar la calificación.',
        confirmButtonColor: '#d33'
      });
    } finally {
      setGuardando(false);
    }
  };

  // 🔵 Navegar al /{rol}/inicio sin cerrar sesión
  const handleSalir = () => {
    const rol = resolveRolBase(location.pathname);
    if (rol) {
      navigate(`/${rol}/inicio`, { replace: true });
    } else {
      navigate('/inicio', { replace: true });
    }
  };

  const materiaSeleccionada = useMemo(
    () => materias.find((m) => String(m.IdMateria) === String(materia)) || null,
    [materias, materia]
  );

  return (
    <div className="container py-4 px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Modificar Calificaciones</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {mensaje && (
        <div className={`alert alert-${mensajeTipo}`} role="alert">
          {mensaje}
        </div>
      )}

      <div className="p-4 bg-light rounded shadow-sm">
        <form className="row g-3" id="formularioCalificaciones" onSubmit={manejarGuardar}>
          <div className="col-md-4">
            <label className="form-label">Grupo</label>
            <select
              className="form-select"
              value={grupo}
              onChange={manejarCambioGrupo}
              disabled={loadingGrupos || guardando}
            >
              <option value="">Selecciona...</option>
              {grupos.map((g) => (
                <option key={g.IdGrupo} value={g.IdGrupo}>
                  {`${g.Grado ?? ''}° ${g.CicloEscolar ? `- ${g.CicloEscolar}` : ''}`.trim() || `Grupo ${g.IdGrupo}`}
                </option>
              ))}
            </select>
            {loadingGrupos && <small className="text-muted">Cargando grupos...</small>}
          </div>

          <div className="col-md-4">
            <label className="form-label">Materia</label>
            <select
              className="form-select"
              value={materia}
              onChange={manejarCambioMateria}
              disabled={!grupo || loadingMaterias || guardando}
            >
              <option value="">Selecciona...</option>
              {materias.map((m) => (
                <option key={m.IdMateria} value={m.IdMateria}>
                  {m.NombreMateria}
                </option>
              ))}
            </select>
            {loadingMaterias && <small className="text-muted">Cargando materias...</small>}
          </div>

          <div className="col-md-4">
            <label className="form-label">Trimestre</label>
            <select
              className="form-select"
              value={trimestre}
              onChange={manejarCambioTrimestre}
              disabled={!grupo || guardando}
            >
              <option value="">Selecciona...</option>
              <option value="1">1° Trimestre</option>
              <option value="2">2° Trimestre</option>
              <option value="3">3° Trimestre</option>
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Fase</label>
            <input
              placeholder="Fase 3, 4 o 5"
              disabled
              type="text"
              className="form-control"
              value={fase}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Alumno</label>
            <select
              className="form-select"
              value={alumno}
              onChange={manejarCambioAlumno}
              disabled={!grupo || loadingAlumnos || guardando}
            >
              <option value="">Selecciona...</option>
              {alumnos.map((a) => (
                <option key={a.IdAlumno} value={a.IdAlumno}>
                  {a.NombreCompleto}
                </option>
              ))}
            </select>
            {loadingAlumnos && <small className="text-muted">Cargando alumnos...</small>}
          </div>

          <div className="col-md-4">
            <label className="form-label">Calificación</label>
            <select
              className="form-select"
              value={calificacion}
              onChange={(e) => setCalificacion(e.target.value)}
              disabled={!grupo || !materia || !trimestre || !alumno || guardando}
            >
              {!calificacion && <option value="">Selecciona...</option>}
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10">10</option>
            </select>
          </div>

          <div className="col-12">
            <label className="form-label">Observaciones</label>
            <textarea
              className="form-control"
              id="observaciones"
              rows="4"
              placeholder="Observaciones del desempeño del alumno..."
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              disabled={!grupo || !materia || !trimestre || !alumno || guardando}
            ></textarea>
          </div>

          <div className="col-12">
            <label className="form-label">Sugerencias</label>
            <textarea
              className="form-control"
              id="sugerencias"
              rows="3"
              placeholder="Recomendaciones o mejoras sugeridas..."
              value={sugerencias}
              onChange={(e) => setSugerencias(e.target.value)}
              disabled={!grupo || !materia || !trimestre || !alumno || guardando}
            ></textarea>
          </div>

          <div className="col-12 d-flex justify-content-end gap-3">
            <button type="button" className="btn btn-warning px-4" onClick={limpiarCampos} disabled={guardando}>
              Nuevo
            </button>
            <button type="submit" className="btn btn-primary px-4" disabled={guardando}>
              {guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>

      <div className="d-flex justify-content-center gap-3 mt-4">
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
          Salir
        </button>
      </div>

      <div className="tabla-calificaciones mt-5">
        <h5 className="text-primary">Resumen de la calificación seleccionada</h5>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Materia</th>
                <th>Calificación</th>
                <th>Observaciones</th>
                <th>Sugerencias</th>
              </tr>
            </thead>
            <tbody>
              {cargandoCalificacion ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted">Cargando información...</td>
                </tr>
              ) : grupo && materia && alumno ? (
                <tr>
                  <td>{materiaSeleccionada?.NombreMateria ?? '—'}</td>
                  <td>{calificacion || '—'}</td>
                  <td>{observaciones || '—'}</td>
                  <td>{sugerencias || '—'}</td>
                </tr>
              ) : (
                <tr>
                  <td colSpan="4" className="text-center text-muted">Selecciona grupo, materia, trimestre y alumno para visualizar la información.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}

export default RegistrarCalificaciones;
